/**
 * StoneListingGrid - Stone Investment themed Listing Grid Component
 * 
 * Grid layout with Stone Investment's luxury portfolio aesthetic:
 * - Editorial grid layouts
 * - Skeleton loader with Stone styling
 * - Empty state with elegant typography
 */

'use client';

import type { ListingCardData } from '@/components/listings/types';
import { StoneListingCard } from './StoneListingCard';

export interface StoneListingGridProps {
  listings: ListingCardData[];
  basePath: string;
  variant?: 'grid' | 'list';
  columns?: 1 | 2 | 3 | 4;
  showAgent?: boolean;
  showAgentAvatar?: boolean;
  showStatus?: boolean;
  showWishlistButton?: boolean;
  wishlistEntityType?: 'PROPERTY' | 'LISTING';
  locale?: string;
  emptyMessage?: string;
  emptyDescription?: string;
  className?: string;
}

export function StoneListingGrid({
  listings,
  basePath,
  variant = 'grid',
  columns = 2,
  showAgent = false,
  showAgentAvatar = false,
  showStatus = true,
  showWishlistButton = true,
  wishlistEntityType = 'PROPERTY',
  locale = 'en',
  emptyMessage = 'No properties found',
  emptyDescription = 'Explore our collection or refine your search criteria.',
  className = '',
}: StoneListingGridProps) {
  // Empty state - Stone Investment styled
  if (listings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
        <div className="w-20 h-20 border border-stone-300 flex items-center justify-center mb-8">
          <svg className="w-10 h-10 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h3 className="font-serif text-3xl text-stone-900 mb-3 italic">{emptyMessage}</h3>
        <p className="text-stone-500 font-sans text-sm tracking-wide max-w-md">{emptyDescription}</p>
      </div>
    );
  }

  // Grid layout classes
  const gridClasses = variant === 'list' 
    ? 'flex flex-col gap-8'
    : `grid gap-12 ${
        columns === 1 ? 'grid-cols-1' :
        columns === 2 ? 'grid-cols-1 md:grid-cols-2' :
        columns === 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' :
        'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
      }`;

  return (
    <div className={`${gridClasses} ${className}`}>
      {listings.map((listing) => (
        <StoneListingCard
          key={listing.id}
          listing={listing}
          href={`${basePath}/properties/${listing.property.id}`}
          variant={variant === 'list' ? 'landscape' : 'default'}
          showAgent={showAgent}
          showAgentAvatar={showAgentAvatar}
          showStatus={showStatus}
          showWishlistButton={showWishlistButton}
          wishlistEntityType={wishlistEntityType}
          locale={locale}
        />
      ))}
    </div>
  );
}

/**
 * Loading skeleton for StoneListingGrid
 */
export function StoneListingGridSkeleton({
  count = 4,
  columns = 2,
  className = '',
}: {
  count?: number;
  columns?: 1 | 2 | 3 | 4;
  className?: string;
}) {
  const gridClasses = `grid gap-12 ${
    columns === 1 ? 'grid-cols-1' :
    columns === 2 ? 'grid-cols-1 md:grid-cols-2' :
    columns === 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' :
    'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
  }`;

  return (
    <div className={`${gridClasses} ${className}`}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="flex flex-col gap-6 animate-pulse">
          {/* Image skeleton - square corners */}
          <div className="aspect-video bg-gradient-to-br from-stone-200 to-stone-100" />
          
          {/* Content skeleton */}
          <div className="flex flex-col gap-4 border-t border-stone-200 pt-6">
            {/* Title and price row */}
            <div className="flex justify-between items-baseline">
              <div className="h-7 bg-stone-200 w-2/3" />
              <div className="h-5 bg-stone-200 w-20" />
            </div>
            
            {/* Details skeleton */}
            <div className="flex gap-4">
              <div className="h-4 bg-stone-100 w-16" />
              <div className="h-4 bg-stone-100 w-12" />
              <div className="h-4 bg-stone-100 w-12" />
              <div className="h-4 bg-stone-100 w-20" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default StoneListingGrid;
